import { MapPin, Clock, Navigation } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { siteData } from "../data/mock";

export const LocationsSection = () => {
  return (
    <section
      id="locations"
      aria-labelledby="locations-heading"
      className="py-20 lg:py-28"
      style={{ background: '#FFF9F2' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="font-mono text-[11px] uppercase tracking-widest text-stone-400 block mb-3">Visit Us</span>
          <h2
            id="locations-heading"
            className="text-3xl sm:text-4xl font-semibold text-[#232323] mb-4 tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif' }}
          >
            Location & Hours
          </h2>
          <p className="text-lg text-stone-500 leading-relaxed">
            Serving Orange County with accessible, state-of-the-art facilities.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {siteData.locations.map((location) => (
            <Card
              key={location.id}
              className="border border-stone-100 hover:border-[#C5D4C0]/60 shadow-sm hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden group bg-white"
            >
              <div className="h-44 relative overflow-hidden bg-stone-100">
                <iframe
                  title={`Map of ${location.name} clinic location`}
                  src={`https://maps.google.com/maps?q=${location.mapQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  aria-label={`Interactive map showing ${location.name} clinic at ${location.address}`}
                />
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-white/90 text-[#5E8C61] text-[11px] font-semibold shadow-sm border-0 backdrop-blur-sm">
                    {location.name}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-[#232323]">
                    {location.name}
                  </h3>
                  {location.facility && (
                    <p className="text-sm font-medium text-[#5E8C61] mt-1 italic opacity-80">
                      {location.facility}
                    </p>
                  )}
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex items-start gap-3">
                    <MapPin
                      className="w-4 h-4 text-[#5E8C61] mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm text-stone-700 font-medium">
                        {location.address}
                      </p>
                      <p className="text-sm text-stone-500 mt-0.5">{location.city}</p>
                      {location.crossStreet && (
                        <p className="text-[12px] text-stone-400 mt-1 italic">
                          Cross: {location.crossStreet}
                        </p>
                      )}
                      {location.parkingInfo && (
                        <p className="text-[11px] text-[#BCA182] mt-2 font-medium bg-[#BCA182]/5 px-2 py-1 rounded">
                          🅿️ {location.parkingInfo}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock
                      className="w-4 h-4 text-[#BCA182] mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    <p className="text-sm text-stone-700">{location.hours}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Button
                    asChild
                    variant="ghost"
                    className="w-full text-[#5E8C61] hover:text-[#4A7A4D] hover:bg-[#F0F4ED] h-10 text-sm font-medium transition-all duration-200"
                  >
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                        location.address + ", " + location.city
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Get directions to ${location.name} clinic at ${location.address}`}
                    >
                      <Navigation className="w-4 h-4 mr-1.5" />
                      Get Directions
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
